import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import React, { useState } from 'react';
import { Button, Offcanvas, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import Select, { type OptionProps } from 'react-select';
import { ApiResponse } from '@/app/common/types/common';
import gscApi, {
  useDeleteAccountMutation,
  useRefreshHostNamesMutation
} from '@/app/KeywordResearch/api/gscApi';
import { GscHostName } from '@/app/KeywordResearch/types/gscApiTypes';
import { errorMessage, successMessage } from '@/utils/toast';
import ConnectionNameModal from '../keyword-gsc/ConnectionNameModal';
import CancelModal from '@/app/common/components/CancelModal';
import { getColor } from '@/helpers/utils';

type SelectOption = {
  label: string;
  value: string;
};

const getGscSettingsSelectStyles = () => ({
  control: (baseStyles: any) => ({
    ...baseStyles,
    direction: 'ltr !important',
    paddingLeft: '10px',
    paddingRight: '0',
    color: 'var(--falcon-emphasise) !important',
    boxShadow: 'none !important',
    width: '100% !important',
    cursor: 'pointer',
    borderRadius: '5px 5px 5px 5px !important'
  }),
  input: (baseStyles: any) => ({
    ...baseStyles,
    direction: 'ltr !important',
    color: 'var(--falcon-emphasise) !important',
    boxShadow: 'none !important'
  })
});

type GSCSettingsDrawerProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  account: SelectOption | null;
  setFieldValue: (field: 'account', value: SelectOption | null) => void;
  hostNames: ApiResponse<GscHostName[]> | undefined;
};

const GSCSettingsDrawer = ({
  isOpen,
  setIsOpen,
  account,
  setFieldValue,
  hostNames
}: GSCSettingsDrawerProps) => {
  const [show, setShow] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const dispatch = useDispatch();
  const [refreshHostNames, { isLoading: isLoadingRefresh }] =
    useRefreshHostNamesMutation();
  const [deleteAccount] = useDeleteAccountMutation();

  return (
    <Offcanvas
      onShow={() => {
        const offcanvasElement = document.querySelector('.offcanvas');
        if (offcanvasElement) {
          offcanvasElement.removeAttribute('tabindex');
        }
      }}
      show={isOpen}
      onHide={() => setIsOpen(false)}
      style={{ backgroundColor: 'var(--falcon-body-bg)' }}
    >
      <CancelModal
        submitTitle="آره، حذف بشه"
        title={<>حذف اکانت</>}
        show={showDelete}
        setShow={setShowDelete}
        submitFunc={() => {
          if (!account?.value) return;

          deleteAccount(account.value).then(() => {
            dispatch(
              gscApi.util.invalidateTags(['userGscHostNames', 'userGscHistory'])
            );
          });
        }}
        desc={
          'آیا مطمئن هستید میخواهید اکانت ' + account?.label + ' را حذف کنید'
        }
      />
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            تنظیمات GSC
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <hr className="my-0" />
      <Offcanvas.Body className="pb-5 pt-0">
        <ConnectionNameModal show={show} setShow={setShow} />
        <div className="d-flex mt-3 flex-column gap-3">
          <div>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setShow(true);
              }}
              className="border-300 d-flex align-items-center"
            >
              <FontAwesomeIcon icon="link" />
              <span className="ms-1">اتصال مجدد به GSC</span>
              <HelpTooltip
                placement="top"
                color={getColor('black')}
                html={
                  <>
                    اگر قصد دارید دسترسی به اکانت دیگر گوگل یا دسترسی مجدد به
                    همان اکانت گوگل رو بدید از این گزینه استفاده کنید.
                  </>
                }
              />
            </Button>
          </div>

          <div className="d-flex mt-3 flex-row align-items-center flex-wrap gap-3">
            <div style={{ width: 205, zIndex: 5 }}>
              <Select<SelectOption>
                placeholder="انتخاب اکانت"
                menuShouldScrollIntoView={true}
                closeMenuOnSelect={true}
                isSearchable={true}
                onChange={e => {
                  setFieldValue('account', e);
                }}
                value={account}
                options={
                  hostNames?.data
                    ? Array.from(
                        hostNames.data
                          .reduce((map, host) => {
                            if (!map.has(host.account_title)) {
                              map.set(host.account_title, {
                                label: host.account_title,
                                value: host.account_id
                              });
                            }
                            return map;
                          }, new Map<string, SelectOption>())
                          .values()
                      )
                    : []
                }
                classNamePrefix="react-select"
                maxMenuHeight={350}
                noOptionsMessage={() => 'اکانتی یافت نشد!!!'}
                styles={getGscSettingsSelectStyles()}
                components={{
                  Option: OptionLayout
                }}
              />
            </div>
            <Button
              onClick={() => {
                if (!account?.value) return;

                refreshHostNames(account.value)
                  .unwrap()
                  .then(response => {
                    if (response?.status === 'ok') {
                      dispatch(
                        gscApi.util.invalidateTags(['userGscHostNames'])
                      );
                      successMessage('عملیات با موفقیت انجام شد.');
                      return;
                    }else if (response?.status === 'no_domains') {
                      errorMessage('اکانت شما اجازه دسترسی به هیچ سایتی را در سرچ کنسول گوگل ندارد.');
                      return;
                    }else if (response?.status === 'access_denied') {
                      errorMessage('دسترسی به سرچ کنسول گوگل شما منقضی شده است. لطفا دوباره دسترسی بدهید.');
                      return;
                    }else {
                      errorMessage('در انجام عملیات خطایی رخ داد.');
                      return;
                    }
                  })
                  .catch(() => {
                    errorMessage('در انجام عملیات خطایی رخ داد.');
                  });
              }}
              disabled={isLoadingRefresh || !account?.value}
              variant="outline-secondary"
              className="border-300 d-flex align-items-center"
            >
              <span className="ms-1">
                {isLoadingRefresh ? (
                  <>
                    <Spinner
                      className="me-1"
                      style={{
                        width: '16px',
                        height: '16px'
                      }}
                    />
                    درحال به‌روزرسانی
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon
                      title=" به‌روزرسانی دامنه های اکانت"
                      className="me-1 d-block d-lg-none"
                      icon="sync-alt"
                    />
                    <span className="d-none d-lg-inline-block">
                      به‌روزرسانی دامنه های اکانت
                    </span>
                  </>
                )}
              </span>
              <HelpTooltip
                color={getColor('black')}
                placement="top"
                html={
                  <>
                    اگر دامنه جدید به سرچ کنسول گوگل اضافه کردید یا دسترسی جدید
                    به یک اکانت گوگل داده شده از این گزینه استفاده کنید تا لیست
                    دامنه ها در سمت چپ بروز بشه.
                  </>
                }
              />
            </Button>
            <Button
              onClick={() => {
                setShowDelete(true);
              }}
              disabled={!account?.value}
              variant="danger"
              className="d-flex align-items-center gap-2 justify-content-center"
            >
              <FontAwesomeIcon icon="trash-alt" className="fs-0" />
              حذف اکانت
            </Button>
          </div>
        </div>
      </Offcanvas.Body>
      <style>
        {`
 .offcanvas{
     width:900px !important;
      max-width:100% !important;
    }
  .offcanvas img {
    margin: 0 auto;
    max-width: 100%;
    height: auto;
    display: flex;
    }
 `}
      </style>
    </Offcanvas>
  );
};

export default GSCSettingsDrawer;

function OptionLayout(props: OptionProps<SelectOption>) {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
        .customized-searchbox-option:hover{
          background-color:var(--falcon-gray-link-hover-300) !important;
        }
        .customized-searchbox-option{
          background-color:var(--falcon-tertiary-bg) !important;
        }
      `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          direction: 'ltr',
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className="p-2 cursor-pointer border border-1 customized-searchbox-option"
      >
        <div className="fs-0 text-900 d-flex justify-content-between">
          {props.data.label}
        </div>
      </div>
    </>
  );
}
